// src/components/editor/EditorComponentItem.jsx
import React from 'react';
import {
  GripVertical,
  Trash2,
  Check,
  Bold,
  Type,
  Image,
  Square
} from 'lucide-react';

const componentTypes = [
  {
    type: 'title',
    icon: () => <Type className="w-4 h-4 text-blue-500" />,
    label: '标题',
    defaultContent: { text: '这是一个标题', fontSize: 'text-2xl', color: 'text-gray-900', isBold: true }
  },
  {
    type: 'text',
    icon: () => <Type className="w-4 h-4 text-blue-500" />,
    label: '文本',
    defaultContent: { text: '这是一段文本内容', fontSize: 'text-base', color: 'text-gray-700', isBold: false }
  },
  {
    type: 'image',
    icon: () => <Image className="w-4 h-4 text-blue-500" />,
    label: '图片',
    defaultContent: 'https://placehold.co/400x200/6366f1/ffffff?text=图片'
  },
  {
    type: 'button',
    icon: () => <Square className="w-4 h-4 text-blue-500" />,
    label: '按钮',
    defaultContent: { text: '点击按钮', action: 'none', link: '', copyText: '' }
  }
];

const fontSizeOptions = [
  { label: '超小', value: 'text-xs' },
  { label: '小', value: 'text-sm' },
  { label: '正常', value: 'text-base' },
  { label: '中等', value: 'text-lg' },
  { label: '大', value: 'text-xl' },
  { label: '超大', value: 'text-2xl' },
  { label: '特大', value: 'text-3xl' },
  { label: '巨大', value: 'text-4xl' }
];

const colorOptions = [
  { label: '黑色', value: 'text-gray-900', bg: 'bg-gray-900' },
  { label: '深灰', value: 'text-gray-700', bg: 'bg-gray-700' },
  { label: '灰色', value: 'text-gray-500', bg: 'bg-gray-500' },
  { label: '蓝色', value: 'text-blue-600', bg: 'bg-blue-600' },
  { label: '绿色', value: 'text-green-600', bg: 'bg-green-600' },
  { label: '红色', value: 'text-red-600', bg: 'bg-red-600' },
  { label: '紫色', value: 'text-purple-600', bg: 'bg-purple-600' },
  { label: '橙色', value: 'text-orange-600', bg: 'bg-orange-600' }
];

export default function EditorComponentItem({
  component,
  onDelete,
  onUpdate,
  onDragStart,
  onDragEnd
}) {
  const compDef = componentTypes.find(c => c.type === component.type);
  const ComponentIcon = compDef.icon;

  return (
    <div
      className="draggable-component bg-white rounded-lg shadow-md p-4 mb-3 border border-gray-200 hover:shadow-lg transition-all duration-200 cursor-move"
      draggable
      onDragStart={(e) => onDragStart(e, component)}
      onDragEnd={onDragEnd}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <GripVertical className="text-gray-400 cursor-grab" />
          <ComponentIcon />
          <span className="text-sm font-medium text-gray-600 capitalize">
            {compDef.label}
          </span>
        </div>
        <button
          onClick={() => onDelete(component.id)}
          className="p-1 hover:bg-red-100 rounded transition-colors"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>

      {component.type === 'image' ? (
        <div className="space-y-2">
          <img
            src={component.content}
            alt="预览"
            className="w-full h-32 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => onUpdate(component.id, component.content)} // 用于放大
          />
          <input
            type="text"
            value={component.content}
            onChange={(e) => onUpdate(component.id, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="输入图片URL"
          />
          <p className="text-xs text-gray-500">点击图片可放大查看</p>
        </div>
      ) : component.type === 'button' ? (
        <div className="space-y-3">
          <input
            type="text"
            value={component.content.text}
            onChange={(e) => onUpdate(component.id, { ...component.content, text: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="按钮文字"
          />
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">按钮功能</label>
            <select
              value={component.content.action}
              onChange={(e) => onUpdate(component.id, { ...component.content, action: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="none">无操作</option>
              <option value="copy">复制内容</option>
              <option value="link">跳转链接</option>
            </select>
          </div>
          {component.content.action === 'copy' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">要复制的内容</label>
              <textarea
                value={component.content.copyText}
                onChange={(e) => onUpdate(component.id, { ...component.content, copyText: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows="2"
                placeholder="输入要复制的文本内容"
              />
            </div>
          )}
          {component.content.action === 'link' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">目标链接</label>
              <input
                type="url"
                value={component.content.link}
                onChange={(e) => onUpdate(component.id, { ...component.content, link: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://example.com"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            value={component.content.text}
            onChange={(e) => onUpdate(component.id, { ...component.content, text: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={component.type === 'title' ? 2 : 4}
            placeholder={`输入${compDef.label}内容`}
          />
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">字体大小</label>
            <select
              value={component.content.fontSize}
              onChange={(e) => onUpdate(component.id, { ...component.content, fontSize: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {fontSizeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">文字颜色</label>
            <div className="grid grid-cols-4 gap-2">
              {colorOptions.map(color => (
                <button
                  key={color.value}
                  onClick={() => onUpdate(component.id, { ...component.content, color: color.value })}
                  className={`w-full h-8 rounded-md flex items-center justify-center ${color.bg} ${component.content.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
                  title={color.label}
                >
                  {component.content.color === color.value && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={component.content.isBold ?? false}
                onChange={(e) => onUpdate(component.id, { ...component.content, isBold: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs font-medium text-gray-700 flex items-center space-x-1">
                <Bold className="w-3 h-3" />
                <span>粗体</span>
              </span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

// 导出常量供其他地方使用
export { componentTypes, fontSizeOptions, colorOptions };